import type { ISO2 } from '@/constants/countries';
import { useCompanySummary } from '@/pages/CompanyReport/components/OverviewSection/hooks/useCompanySummary/useCompanySummary';
import { getPriceTypeText } from '@/pages/StockPage/utils';

import { useFlag } from '@simplywallst/unleash';

/**
 * Hook to check if a company's country matches the provided countryIso.
 * @param canonicalUrl - The canonical URL of the company
 * @param countryIso - The country ISO code to check against
 * @returns { isMatch, isLoading, stockLabel, stockUrl }
 */
export const useCompanyCountryMatch = (
  canonicalUrl: string,
  allowedExchangeCountryIso: string
) => {
  const {
    tickerSymbol,
    exchangeSymbol,
    status,
    exchangeCountryIso: companyExchangeCountryIso,
  } = useCompanySummary({ canonicalUrl });

  //Keep ths flag disabled while the feature is live so it's SSRed.
  //Turn this flag on in prod env to disable user access to the stock page.
  const disableStockPage = useFlag('mono-stock-page-killswitch');

  if (status !== 'success') {
    return { isMatch: false, status, stockLabel: '', stockUrl: '' };
  }

  const isMatch =
    !disableStockPage.enabled &&
    !!companyExchangeCountryIso &&
    !!allowedExchangeCountryIso &&
    companyExchangeCountryIso.toLowerCase() ===
      allowedExchangeCountryIso.toLowerCase();

  const priceTypeText = getPriceTypeText(companyExchangeCountryIso as ISO2);

  const stockLabel = priceTypeText.priceLabel;

  const stockUrl = isMatch
    ? `/stock/${exchangeSymbol?.toLowerCase()}/${tickerSymbol?.toLowerCase()}`
    : '';

  return { isMatch, status, stockLabel, stockUrl };
};
